import { Component, Element, h, Host, Prop, State, Watch, Method } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { RteRendererSchemeDefinition, RteRendererSchemeName, RteSource } from "./constants/rte-renderer-types";
import {
  RTESOURCE_QUILL,
  RTE_RENDERER_SCHEME_CONFIG,
  RTE_RENDERER_SCHEME_MAP
} from "./constants/rte-renderer-constants";
import pfx from "../../utils/style-class-prefix-helper";
import { RteRendererSchemeConfiguration } from "../../constants/colors";

/**
 * @slot info-icon - Holds a single eon-ui-info-icon element
 */
@Component({
  tag: "eon-ui-rte-renderer",
  styleUrl: "rte-renderer.scss",
  shadow: true
})
export class RteRenderer implements ColorableComponent<RteRendererSchemeConfiguration<RteRendererSchemeDefinition>> {
  schemeConfig: RteRendererSchemeConfiguration<RteRendererSchemeDefinition> = RTE_RENDERER_SCHEME_CONFIG;
  schemeMap = RTE_RENDERER_SCHEME_MAP;
  rendererContent: any;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: RteRendererSchemeDefinition;
  @State() isInfoIconAvailable: boolean;

  @Element() hostElement: HTMLEonUiRteRendererElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: RteRendererSchemeName;

  /**
   * Defines the RTE source of the content to render.
   */
  @Prop() rteSource: RteSource = RTESOURCE_QUILL;

  /**
   * Content of rte renderer as html string
   */
  @Prop() content: string;

  /**
   * Set Content of Rich Text Renderer
   * @param value HTML String that has to be inserted and styled
   */
  @Method()
  async setContent(value: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        this.rendererContent.innerHTML = value;
        this.setPolyfillCssPrefix(this.rendererContent);
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  componentWillLoad() {
    this.applyScheme();
    this.isInfoIconAvailable = !!this.hostElement.querySelector('[slot="info-icon"]');
  }

  componentDidLoad() {
    this.setRendererContent();
    this.setCssVariables();
  }

  @Watch("scheme")
  applyScheme(): void {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  @Watch("content")
  setRendererContent(): void {
    this.rendererContent.innerHTML = this.content;
    this.setPolyfillCssPrefix(this.rendererContent);
  }

  setPolyfillCssPrefix(content): any {
    let tags = [
      "div",
      "span",
      "p",
      "ol",
      "ul",
      "li",
      "blockquote",
      "strong",
      "sub",
      "sup",
      "em",
      "a",
      "h4",
      "h5",
      "h6"
    ];
    tags.forEach((tag) => {
      content.querySelectorAll(tag).forEach((child) => {
        child.classList.add("sc-eon-ui-rte-renderer");
      });
    });

    return content;
  }

  render() {
    return (
      <Host
        class={pfx({
          ["info-icon-display"]: this.isInfoIconAvailable
        })}
      >
        <div
          class={pfx({
            ["renderer-content"]: true,
            [`rte-source-${this.rteSource}`]: true.valueOf
          })}
          ref={(el) => (this.rendererContent = el as HTMLElement)}
        ></div>
        {this.isInfoIconAvailable && (
          <div class={pfx("rte-renderer-info-icon")}>
            <slot name="info-icon" />
          </div>
        )}
      </Host>
    );
  }
}
